/**
 * MetricsDevPage - owner-only usage metrics dashboard.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@/components/shared';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import { supabase } from '@services/supabase';
import { getApiUrl } from '@services/sync';
import './MetricsDevPage.css';
import { MetricsDevAccessPanel } from './MetricsDevAccessPanel';

interface MetricsOverview {
  totalLoggedInUsers: number;
  trackedUsers: number;
  ownerUsers: number;
  scopedUsers: number;
  activeNow: number;
  activeToday: number;
  active7d: number;
  setupStarted: number;
  setupComplete: number;
  importedUsers: number;
  totalPageViews: number;
  totalEvents: number;
  totalSessions: number;
  trackedSessions: number;
  sessionPageViews: number;
  importFailures: number;
}

interface MetricsUser {
  userId: string;
  email: string | null;
  isOwner: boolean;
  source: 'auth' | 'tracked' | 'merged';
  authCreatedAt: string | null;
  lastSignInAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastPage: string | null;
  lastEventType: string | null;
  setupStage: string;
  sessionCount: number;
  pageViews: number;
  totalEvents: number;
  accountCount: number;
  maxAccountCount: number;
  transactionCount: number;
  maxTransactionCount: number;
  categoryCount: number;
  budgetCount: number;
  scheduledTransactionCount: number;
  pendingSyncCount: number;
  importedData: boolean;
  importCount: number;
  firstAccountAt: string | null;
  firstTransactionAt: string | null;
  firstImportAt: string | null;
  lastSetupAt: string | null;
  lastSessionId: string | null;
  lastDeviceId: string | null;
  lastUserAgent: string | null;
  lastIp: string | null;
  timezone: string | null;
  appVersion: string | null;
}

interface MetricsSession {
  id: string;
  userId: string;
  email: string | null;
  isOwner: boolean;
  status: 'active' | 'idle';
  idleSeconds: number;
  deviceId: string | null;
  startedAt: string;
  lastSeenAt: string;
  lastPage: string | null;
  pageViews: number;
  events: number;
  userAgent: string | null;
  ip: string | null;
  timezone: string | null;
}

interface TopPage {
  route: string;
  title: string;
  views: number;
  uniqueUsers: number;
  lastViewedAt: string | null;
}

interface RecentEvent {
  id: string;
  userId: string;
  email: string | null;
  isOwner: boolean;
  eventType: string;
  route: string | null;
  occurredAt: string;
  metadata: unknown;
}

interface DailyActive {
  day: string;
  activeUsers: number;
  events: number;
}

interface MetricsResponse {
  asOf: string;
  includeOwners: boolean;
  rangeDays: number;
  activeWindowMinutes: number;
  warnings?: string[];
  overview: MetricsOverview;
  setupFunnel: {
    signedIn: number;
    accountsAdded: number;
    transactionsOrImport: number;
    imported: number;
    active7d: number;
  };
  users: MetricsUser[];
  sessions: MetricsSession[];
  topPages: TopPage[];
  importFailures: RecentEvent[];
  recentEvents: RecentEvent[];
  dailyActive: DailyActive[];
}

function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function formatIdle(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}h ${remainder}m`;
}

function labelForStage(stage: string): string {
  if (stage === 'active') return 'Active';
  if (stage === 'accounts_added') return 'Accounts added';
  return 'Signed in';
}

function labelForSource(source: MetricsUser['source']): string {
  if (source === 'auth') return 'auth only';
  if (source === 'merged') return 'auth + metrics';
  return 'metrics only';
}

function identity(email: string | null, userId: string): string {
  return email || `${userId.slice(0, 8)}...`;
}

function userAgentShort(userAgent: string | null): string {
  if (!userAgent) return '-';
  if (userAgent.includes('Chrome/')) return 'Chrome';
  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) return 'Safari';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('curl/')) return 'curl';
  return userAgent.slice(0, 80);
}

function importSummaryFromEvent(metadata: unknown): Record<string, unknown> {
  if (!metadata || typeof metadata !== 'object') return {};
  const root = metadata as Record<string, unknown>;
  const summary = root.importSummary;
  if (!summary || typeof summary !== 'object') return {};
  return summary as Record<string, unknown>;
}

function metricToken(value: unknown, fallback = '-'): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  return value.replace(/_/g, ' ');
}

function metricNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

// Users not seen for this many days are tucked into a collapsed list so the
// main table stays focused on people who actually use the app.
const INACTIVE_DAYS = 32;
const NEW_USER_DAYS = 7;

function lastActivityTime(user: MetricsUser): number {
  const candidates = [user.lastSeenAt, user.lastSignInAt, user.authCreatedAt, user.firstSeenAt];
  for (const value of candidates) {
    if (!value) continue;
    const time = new Date(value).getTime();
    if (Number.isFinite(time)) return time;
  }
  return 0;
}

function isNewUser(user: MetricsUser): boolean {
  const created = user.authCreatedAt || user.firstSeenAt;
  if (!created) return false;
  const time = new Date(created).getTime();
  if (!Number.isFinite(time)) return false;
  return Date.now() - time < NEW_USER_DAYS * 86400000;
}

function UserRow({ user }: { user: MetricsUser }) {
  return (
    <tr>
      <td>
        <div className="metrics-user-cell">
          <strong>
            {identity(user.email, user.userId)}
            {isNewUser(user) && <span className="metrics-pill-new">new</span>}
          </strong>
          <span>{user.isOwner ? 'owner' : user.timezone || '-'}</span>
        </div>
      </td>
      <td>
        <span className={`metrics-source ${user.source}`}>
          {labelForSource(user.source)}
        </span>
      </td>
      <td>
        <span className={`metrics-stage ${user.setupStage}`}>
          {labelForStage(user.setupStage)}
        </span>
      </td>
      <td>
        <div className="metrics-stack">
          <span>{formatRelative(user.lastSeenAt)}</span>
          <small>joined {formatDateTime(user.authCreatedAt || user.firstSeenAt)}</small>
        </div>
      </td>
      <td>
        <span className="metrics-mono">
          A{user.accountCount}/T{user.transactionCount}/C{user.categoryCount}
        </span>
        {user.pendingSyncCount > 0 && (
          <span className="metrics-pill-sync" title="Pending sync records">
            sync {formatNumber(user.pendingSyncCount)}
          </span>
        )}
      </td>
      <td>{formatNumber(user.sessionCount)}</td>
      <td>{formatNumber(user.pageViews)}</td>
      <td className="metrics-route">{user.lastPage || (user.source === 'auth' ? 'Auth only' : '-')}</td>
    </tr>
  );
}

export default function MetricsDevPage() {
  const { isOwner, isLoading: authLoading, userEmail } = useOwnerAccess();

  if (authLoading) {
    return <div className="loading">Checking access...</div>;
  }

  if (!isOwner) {
    console.warn('[METRICS_DEV] Unauthorized access attempt:', userEmail);
    return <Navigate to="/" replace />;
  }

  return <MetricsDevContent />;
}

function MetricsDevContent() {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeOwners, setIncludeOwners] = useState(true);
  const [rangeDays, setRangeDays] = useState(365);

  const loadMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        throw new Error('No auth session');
      }

      const params = new URLSearchParams({
        days: String(rangeDays),
        activeMinutes: '15',
        includeOwners: includeOwners ? '1' : '0',
      });

      const response = await fetch(`${getApiUrl()}/api/dev/metrics?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Metrics request failed: ${response.status}`);
      }

      setData(await response.json() as MetricsResponse);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load metrics';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [includeOwners, rangeDays]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadMetrics();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [loadMetrics]);

  const nonOwnerUsers = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, data.overview.totalLoggedInUsers - data.overview.ownerUsers);
  }, [data]);

  // Split users into active vs inactive (not seen for INACTIVE_DAYS+),
  // newest activity first in both lists. The inactive list stays collapsed
  // by default - its count is always visible in the header.
  const [showInactive, setShowInactive] = useState(false);
  const { activeUsers, inactiveUsers } = useMemo(() => {
    const users = [...(data?.users || [])].sort(
      (a, b) => lastActivityTime(b) - lastActivityTime(a)
    );
    const cutoff = Date.now() - INACTIVE_DAYS * 86400000;
    return {
      activeUsers: users.filter((u) => lastActivityTime(u) >= cutoff),
      inactiveUsers: users.filter((u) => lastActivityTime(u) < cutoff),
    };
  }, [data]);

  const maxFunnel = Math.max(
    data?.setupFunnel.signedIn || 0,
    data?.setupFunnel.accountsAdded || 0,
    data?.setupFunnel.transactionsOrImport || 0,
    data?.setupFunnel.imported || 0,
    1
  );

  const maxDaily = Math.max(...(data?.dailyActive.map((day) => day.activeUsers) || [0]), 1);

  return (
    <div className="metrics-dev-page">
      <header className="metrics-dev-header">
        <div>
          <h1 className="metrics-dev-title">metrics_dev</h1>
          <p className="metrics-dev-subtitle">
            Owner-only auth, setup, usage, and active-session view
          </p>
        </div>

        <div className="metrics-dev-actions">
          <label className="metrics-toggle">
            <input
              type="checkbox"
              checked={includeOwners}
              onChange={(event) => setIncludeOwners(event.target.checked)}
            />
            <span>Include owner traffic</span>
          </label>

          <select
            className="metrics-select"
            value={rangeDays}
            onChange={(event) => setRangeDays(Number(event.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={120}>120 days</option>
            <option value={365}>365 days</option>
            <option value={730}>730 days</option>
          </select>

          <Button variant="secondary" size="sm" onClick={loadMetrics} disabled={isLoading}>
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </header>

      {error && (
        <div className="metrics-alert">
          {error}
        </div>
      )}

      <MetricsDevAccessPanel />

      {data && (
        <>
          <div className="metrics-meta-row">
            <span>As of {formatDateTime(data.asOf)}</span>
            <span>{data.includeOwners ? 'All traffic' : 'Owner traffic excluded'}</span>
            <span>Activity window {data.rangeDays}d</span>
            <span>Active now window {data.activeWindowMinutes}m</span>
          </div>

          {data.warnings && data.warnings.length > 0 && (
            <div className="metrics-note">
              {data.warnings.join(' ')}
            </div>
          )}

          <section className="metrics-stat-grid" aria-label="Usage overview">
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.scopedUsers)}</span>
              <span className="metrics-stat-label">Auth Users</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(nonOwnerUsers)}</span>
              <span className="metrics-stat-label">Non-owner Users</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.trackedUsers)}</span>
              <span className="metrics-stat-label">Tracked Activity</span>
            </div>
            <div className="metrics-stat accent">
              <span className="metrics-stat-value">{formatNumber(data.overview.activeNow)}</span>
              <span className="metrics-stat-label">Active Now</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.activeToday)}</span>
              <span className="metrics-stat-label">Active 24h</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.active7d)}</span>
              <span className="metrics-stat-label">Active 7d</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.setupStarted)}</span>
              <span className="metrics-stat-label">Setup Started</span>
            </div>
            <div className="metrics-stat good">
              <span className="metrics-stat-value">{formatNumber(data.overview.setupComplete)}</span>
              <span className="metrics-stat-label">Setup Complete</span>
            </div>
            <div className="metrics-stat">
              <span className="metrics-stat-value">{formatNumber(data.overview.totalPageViews)}</span>
              <span className="metrics-stat-label">Page Views</span>
            </div>
            <div className={`metrics-stat ${data.overview.importFailures > 0 ? 'bad' : ''}`}>
              <span className="metrics-stat-value">{formatNumber(data.overview.importFailures)}</span>
              <span className="metrics-stat-label">Import Failures</span>
            </div>
          </section>

          <section className="metrics-layout metrics-layout-single">
            <div className="metrics-panel metrics-panel-full">
              <div className="metrics-panel-header">
                <h2>Users</h2>
                <span>
                  {activeUsers.length} active · {inactiveUsers.length} inactive {INACTIVE_DAYS}d+
                </span>
              </div>
              <div className="metrics-table-wrap">
                <table className="metrics-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Source</th>
                      <th>Stage</th>
                      <th>Last Seen</th>
                      <th>Counts</th>
                      <th>Sessions</th>
                      <th>Views</th>
                      <th>Last Page</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.length === 0 && inactiveUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="metrics-empty">No users in scope.</td>
                      </tr>
                    ) : activeUsers.map((user) => (
                      <UserRow key={user.userId} user={user} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Inactive users: collapsed by default, count always visible */}
              {inactiveUsers.length > 0 && (
                <>
                  <button
                    className="metrics-inactive-toggle"
                    onClick={() => setShowInactive((v) => !v)}
                    aria-expanded={showInactive}
                  >
                    <svg
                      width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      className={showInactive ? 'open' : ''}
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    Inactive users ({inactiveUsers.length}) - not seen for {INACTIVE_DAYS}+ days
                  </button>
                  {showInactive && (
                    <div className="metrics-table-wrap metrics-inactive-table">
                      <table className="metrics-table">
                        <tbody>
                          {inactiveUsers.map((user) => (
                            <UserRow key={user.userId} user={user} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          <section className="metrics-layout metrics-layout-reverse">
            <div className="metrics-panel">
              <div className="metrics-panel-header">
                <h2>Setup Funnel</h2>
                <span>{rangeDays}d</span>
              </div>
              <div className="metrics-funnel">
                {[
                  ['Signed in', data.setupFunnel.signedIn],
                  ['Accounts added', data.setupFunnel.accountsAdded],
                  ['Transactions/import', data.setupFunnel.transactionsOrImport],
                  ['Imported data', data.setupFunnel.imported],
                  ['Active 7d', data.setupFunnel.active7d],
                ].map(([label, value]) => (
                  <div className="metrics-funnel-row" key={label}>
                    <span>{label}</span>
                    <div className="metrics-funnel-bar">
                      <i style={{ width: `${Math.max(4, (Number(value) / maxFunnel) * 100)}%` }} />
                    </div>
                    <strong>{formatNumber(Number(value))}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="metrics-panel">
              <div className="metrics-panel-header">
                <h2>Import Failures</h2>
                <span>{data.importFailures.length} recent</span>
              </div>
              <div className="metrics-failure-list">
                {data.importFailures.length === 0 ? (
                  <p className="metrics-empty">No import failures in this window.</p>
                ) : data.importFailures.map((event) => {
                  const summary = importSummaryFromEvent(event.metadata);
                  return (
                    <div className="metrics-failure-row" key={event.id}>
                      <span className="metrics-event-time">{formatRelative(event.occurredAt)}</span>
                      <div className="metrics-failure-main">
                        <strong>{identity(event.email, event.userId)}</strong>
                        <span>{metricToken(summary.failureCode, 'import failed')}</span>
                      </div>
                      <span className="metrics-failure-pill">{metricToken(summary.format, 'unknown')}</span>
                      <span className="metrics-failure-pill">{metricToken(summary.stage, 'unknown stage')}</span>
                      <span className="metrics-mono">
                        E{formatNumber(metricNumber(summary.errorCount))}/W{formatNumber(metricNumber(summary.warningCount))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="metrics-layout metrics-layout-single">
            <div className="metrics-panel metrics-panel-full">
              <div className="metrics-panel-header">
                <h2>Connected Clients</h2>
                <span>48h window</span>
              </div>
              <div className="metrics-table-wrap">
                <table className="metrics-table metrics-table-clients">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>User</th>
                      <th>Since</th>
                      <th>Idle</th>
                      <th>Views</th>
                      <th>Last Page</th>
                      <th>IP</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sessions.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="metrics-empty">No sessions in the recent window.</td>
                      </tr>
                    ) : data.sessions.map((session) => (
                      <tr key={session.id}>
                        <td>
                          <span className={`metrics-status ${session.status}`}>
                            {session.status}
                          </span>
                        </td>
                        <td>{identity(session.email, session.userId)}</td>
                        <td>{formatDateTime(session.startedAt)}</td>
                        <td>{formatIdle(session.idleSeconds)}</td>
                        <td>{formatNumber(session.pageViews)}</td>
                        <td className="metrics-route">{session.lastPage || '-'}</td>
                        <td className="metrics-mono metrics-ip">{session.ip || '-'}</td>
                        <td>{userAgentShort(session.userAgent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="metrics-layout metrics-layout-thirds">
            <div className="metrics-panel">
              <div className="metrics-panel-header">
                <h2>Top Pages</h2>
                <span>{data.topPages.length}</span>
              </div>
              <div className="metrics-list">
                {data.topPages.length === 0 ? (
                  <p className="metrics-empty">No page views yet.</p>
                ) : data.topPages.map((page) => (
                  <div className="metrics-list-row" key={page.route}>
                    <div>
                      <strong>{page.title}</strong>
                      <span>{page.route}</span>
                    </div>
                    <div className="metrics-list-values">
                      <strong>{formatNumber(page.views)}</strong>
                      <span>{formatNumber(page.uniqueUsers)} users</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="metrics-panel">
              <div className="metrics-panel-header">
                <h2>Daily Active</h2>
                <span>last 14d</span>
              </div>
              <div className="metrics-trend">
                {data.dailyActive.length === 0 ? (
                  <p className="metrics-empty">No recent events yet.</p>
                ) : data.dailyActive.slice(-14).map((day) => (
                  <div className="metrics-trend-row" key={day.day}>
                    <span>{day.day.slice(5)}</span>
                    <div className="metrics-trend-bar">
                      <i style={{ width: `${Math.max(5, (day.activeUsers / maxDaily) * 100)}%` }} />
                    </div>
                    <strong>{day.activeUsers}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="metrics-panel">
              <div className="metrics-panel-header">
                <h2>Recent Events</h2>
                <span>{data.recentEvents.length}</span>
              </div>
              <div className="metrics-events">
                {data.recentEvents.length === 0 ? (
                  <p className="metrics-empty">No recent events.</p>
                ) : data.recentEvents.slice(0, 18).map((event) => (
                  <div className="metrics-event" key={event.id}>
                    <span className="metrics-event-time">{formatRelative(event.occurredAt)}</span>
                    <strong>{event.eventType}</strong>
                    <span>{event.route || '-'}</span>
                    <small>{event.email || 'unknown'}{event.isOwner ? ' / owner' : ''}</small>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
